import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { adminApi } from '@/lib/adminApi';
import { api, formatApiError } from '@/lib/api';
import { useSort } from '@/lib/useSort';
import { SortableHeader } from '@/components/shared/SortableHeader';
import { useDataLabel } from '@/lib/dataI18n';
import { toast } from 'sonner';
import {
  Plus, Loader2, Pencil, Send, Archive, Minus, ListChecks, Eye,
  ChevronDown, ChevronRight, Search, X, Upload, Library,
} from 'lucide-react';
import { ModuleBanner } from '@/components/admin/ModuleBanner';
import { DuplicateEvaluationButton } from '@/components/library/DuplicateEvaluationButton';
import { LibrarySearchExplorer } from '@/components/library/LibrarySearchExplorer';
import { LibraryListView, LibraryTypeBar } from '@/components/library/LibraryListView';
import { CorrectAnswerRadio } from '@/components/library/CorrectAnswerRadio';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { useApp } from '@/context/AppContext';

const slug = (s) => s.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').slice(0, 60);
const uid = () => Math.random().toString(36).slice(2, 11);

export default function AdminQuestionnaires() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useApp();
  const isAdmin = user?.role === 'hm_admin';
  const tData = useDataLabel();
  const [items, setItems] = useState([]);
  // N (P0 2026-06) — suite filter options DERIVED from the governed suite tags
  // actually present on Library instruments (stale static list omitted
  // talentexit/talentperformance/succession).
  const suiteOptions = useMemo(
    () => [...new Set((items || []).flatMap((it) => it.suite_codes || []))].sort(),
    [items]
  );
  const [vocab, setVocab] = useState(null);
  // Sprint A — Catalog filters (client-side).
  const [revisionDialog, setRevisionDialog] = useState({ q: null, reason: '' });
  const [filters, setFilters] = useState({
    q: new URLSearchParams(window.location.search).get('q') || '',
    status: 'all',
    suite_code: new URLSearchParams(window.location.search).get('suite') || 'all',
    evaluation_type: 'all',
    professional_area: 'all',
    level: 'all',
    language: 'all',
    category: 'all',
  });
  const setF = (k, v) => setFilters((p) => ({ ...p, [k]: v }));
  const resetFilters = () => setFilters({
    q: '', status: 'all', suite_code: 'all', evaluation_type: 'all',
    professional_area: 'all', level: 'all', language: 'all', category: 'all',
  });
  const filteredItems = useMemo(() => {
    const term = (filters.q || '').trim().toLowerCase();
    return items.filter((q) => {
      if (filters.status !== 'all' && q.status !== filters.status) return false;
      if (filters.suite_code !== 'all' && !(q.suite_codes || []).includes(filters.suite_code)) return false;
      if (filters.evaluation_type !== 'all' && (q.evaluation_type || '') !== filters.evaluation_type) return false;
      if (filters.professional_area !== 'all' && (q.professional_area || '') !== filters.professional_area) return false;
      if (filters.level !== 'all' && (q.level || '') !== filters.level) return false;
      if (filters.language !== 'all' && (q.language || '') !== filters.language) return false;
      if (filters.category !== 'all' && (q.category || '') !== filters.category) return false;
      if (term) {
        const blob = `${q.name || ''} ${q.code || ''} ${q.description || ''} ${q.objective || ''}`.toLowerCase();
        if (!blob.includes(term)) return false;
      }
      return true;
    });
  }, [items, filters]);
  const categoryOptions = useMemo(() => {
    const set = new Set();
    items.forEach((q) => { if (q.category) set.add(q.category); });
    return Array.from(set).sort();
  }, [items]);
  const sort = useSort();
  const sorted = useMemo(() => sort.apply(filteredItems, {
    name:   (q) => (q.name || '').toLowerCase(),
    code:   (q) => (q.code || '').toLowerCase(),
    status: (q) => (q.status || '').toLowerCase(),
  }), [filteredItems, sort]);
  const [comps, setComps] = useState([]);
  const [archivedCount, setArchivedCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [editor, setEditor] = useState(null); // {qid, sections, questions}
  const [editorOpen, setEditorOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  // Phase 2 Library cleanup — Tarjetas | Lista toggle + KPI-driven status filter.
  const [viewMode, setViewMode] = useState('cards');
  const [listStatus, setListStatus] = useState('all');
  const showList = (status) => { setListStatus(status); setViewMode('list'); };

  const reload = () => {
    setLoading(true);
    Promise.all([
      adminApi.listQuestionnaires(),
      adminApi.listCompetencies(false),
      vocab ? Promise.resolve(vocab) : adminApi.getLibraryVocab(),
    ])
      .then(([qs, cs, vc]) => { setItems(qs); setComps(cs); setVocab(vc); })
      .catch((e) => toast.error(t('toasts.failedToLoad'), { description: formatApiError(e) }))
      .finally(() => setLoading(false));
    if (user?.role === 'hm_admin') {
      api.get('/admin/trash', { params: { entity_type: 'questionnaire', limit: 1 } })
        .then(({ data }) => setArchivedCount(data.total))
        .catch(() => {});
    }
  };
  useEffect(reload, []);

  const openNew = () => {
    setForm({
      code: '', name: '', description: '', suite_codes: ['talentmap'],
      scoring_model: { type: 'weighted_sections', config: {} },
      interpretation_bands: [], tags: [],
    });
    setOpen(true);
  };

  const create = async () => {
    try {
      const code = form.code || slug(form.name);
      await adminApi.createQuestionnaire({ ...form, code });
      toast.success(t('pages.questionnaires.createdToast'));
      setOpen(false); reload();
    } catch (e) { toast.error(t('toasts.saveFailed'), { description: formatApiError(e) }); }
  };

  const archiveOne = async (q) => {
    try { await adminApi.archiveQuestionnaire(q.id); toast.success(t('toasts.archived')); reload(); }
    catch (e) { toast.error(t('toasts.failed'), { description: formatApiError(e) }); }
  };

  const openEditor = async (q) => {
    try {
      const j = await adminApi.getQuestionnaire(q.id);
      const draft = j.versions[j.versions.length - 1] || { sections: [], questions: [] };
      setEditor({
        qid: q.id, name: q.name,
        sections: draft.sections.map((s) => ({ ...s })),
        questions: draft.questions.map((qq) => ({ ...qq, options: qq.options.map((o) => ({ ...o })) })),
        scoring_model: draft.scoring_model || q.scoring_model,
        interpretation_bands: draft.interpretation_bands || [],
      });
      setEditorOpen(true);
    } catch (e) { toast.error(t('toasts.loadFailed'), { description: formatApiError(e) }); }
  };

  const saveDraft = async () => {
    try {
      await adminApi.createDraftVersion(editor.qid, {
        questionnaire_id: editor.qid, version_number: 0,  // server assigns
        sections: editor.sections, questions: editor.questions,
        scoring_model: editor.scoring_model,
        interpretation_bands: editor.interpretation_bands,
      });
      toast.success(t('toasts.draftVersionSaved')); setEditorOpen(false); reload();
    } catch (e) { toast.error(t('toasts.saveFailed'), { description: formatApiError(e) }); }
  };

  const publishLatest = async (q) => {
    try {
      const j = await adminApi.getQuestionnaire(q.id);
      const last = j.versions[j.versions.length - 1];
      if (last) {
        await adminApi.publishVersion(q.id, last.id);
        toast.success(`Published v${last.version_number}`);
      } else {
        // Studio-authored assessments have no Library versions until first
        // publish — route through the Studio publish pipeline instead.
        try {
          await adminApi.studioPublish(q.id, { publish_target: q.suite_codes?.[0] || 'internal_library' });
        } catch (err) {
          const d = err?.response?.data?.detail;
          if (d?.code === 'checklist_blocks_publish') {
            const isES = (navigator.language || '').startsWith('es');
            const reasons = (d.checklist?.items || [])
              .filter((it) => !it.passed)
              .map((it) => `${isES ? it.label_es : it.label_en}: ${isES ? it.reason_es : it.reason_en}`)
              .join(' · ');
            throw Object.assign(new Error(reasons), { formatted: reasons });
          }
          throw err;
        }
        toast.success(t('pages.questionnaires.publishedToast', { defaultValue: 'Evaluation published.' }));
      }
      reload();
    } catch (e) { toast.error(t('toasts.publishFailed'), { description: e?.formatted || formatApiError(e) }); }
  };

  const approveOne = async (q) => {
    try {
      await api.post(`/admin/questionnaires/${q.id}/approve`);
      toast.success(t('pages.questionnaires.approvedToast', { defaultValue: 'Evaluation approved. You can now publish it.' }));
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitRevision = async () => {
    if ((revisionDialog.reason || '').trim().length < 10) {
      return toast.error(t('pages.questionnaires.revisionReasonShort', { defaultValue: 'Please provide a reason (at least 10 characters).' }));
    }
    try {
      await api.post(`/admin/questionnaires/${revisionDialog.q.id}/request-changes`, { reason: revisionDialog.reason.trim() });
      toast.success(t('pages.questionnaires.revisionToast', { defaultValue: 'Sent back to the author for revision.' }));
      setRevisionDialog({ q: null, reason: '' });
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addSection = () => setEditor({ ...editor, sections: [...editor.sections, { id: uid(), name: t('pages.questionnaires.newSection'), order: editor.sections.length, weight: 1.0 }] });
  const updateSection = (i, patch) => { const next = [...editor.sections]; next[i] = { ...next[i], ...patch }; setEditor({ ...editor, sections: next }); };
  const removeSection = (i) => {
    const sec = editor.sections[i];
    setEditor({ ...editor, sections: editor.sections.filter((_, j) => j !== i),
      questions: editor.questions.filter((q) => q.section_id !== sec.id) });
  };

  const addQuestion = (sectionId) => setEditor({
    ...editor, questions: [...editor.questions, {
      id: uid(), section_id: sectionId, prompt: t('pages.questionnaires.newQuestion'), type: 'single_choice', weight: 1.0,
      options: [{ id: uid(), label: t('pages.questionnaires.optionA'), score: 1, weight: 1.0 }], competency_map: {},
    }],
  });
  const updateQuestion = (qid, patch) => setEditor({ ...editor, questions: editor.questions.map((q) => q.id === qid ? { ...q, ...patch } : q) });
  const removeQuestion = (qid) => setEditor({ ...editor, questions: editor.questions.filter((q) => q.id !== qid) });
  const updateOpt = (qid, oi, patch) => setEditor({
    ...editor, questions: editor.questions.map((q) => {
      if (q.id !== qid) return q;
      const opts = [...q.options]; opts[oi] = { ...opts[oi], ...patch };
      return { ...q, options: opts };
    }),
  });
  const addOpt = (qid) => updateQuestion(qid, { options: [...editor.questions.find((q) => q.id === qid).options, { id: uid(), label: t('pages.questionnaires.newOption'), score: 0, weight: 1.0 }] });
  const removeOpt = (qid, oi) => {
    const q = editor.questions.find((qq) => qq.id === qid);
    updateQuestion(qid, { options: q.options.filter((_, j) => j !== oi) });
  };
  const setCompMap = (qid, code, weight) => {
    const q = editor.questions.find((qq) => qq.id === qid);
    const map = { ...(q.competency_map || {}) };
    if (weight === null) delete map[code]; else map[code] = weight;
    updateQuestion(qid, { competency_map: map });
  };

  return (
    <>
      <Topbar
        title={t('library.pageTitle', { defaultValue: 'Evaluation Library' })}
        subtitle={t('library.pageSubtitle', { defaultValue: 'Create, review and publish reusable evaluations consumed by every suite' })}
      />
      <div className="p-6 lg:p-8 space-y-6" data-testid="admin-qnr-page">
        <ModuleBanner id="library" icon={Library} />
        {/* Prompt 13 — Status KPI boxes (always-visible labels) */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="qnr-kpi-row">
          {(() => {
            const counts = {
              active: items.filter(q => q.status === 'published').length,
              completed: items.filter(q => q.status === 'archived').length,
              pending: items.filter(q => ['submitted_for_review', 'needs_revision', 'approved'].includes(q.status)).length,
              drafts: items.filter(q => q.status === 'draft').length,
            };
            const boxes = [
              { key: 'active',    label: t('common.questionnaireKpis.active'),    value: counts.active,    tone: 'text-success',      testid: 'qnr-kpi-active',
                onClick: () => showList('published') },
              { key: 'completed', label: t('common.questionnaireKpis.completed'), value: archivedCount ?? counts.completed, tone: 'text-info', testid: 'qnr-kpi-completed',
                onClick: user?.role === 'hm_admin' ? () => navigate('/admin/trash') : undefined },
              { key: 'pending',   label: t('common.questionnaireKpis.pending'),   value: counts.pending,   tone: 'text-warning',      testid: 'qnr-kpi-pending',
                onClick: () => showList('pending') },
              { key: 'drafts',    label: t('common.questionnaireKpis.drafts'),    value: counts.drafts,    tone: 'text-muted-foreground', testid: 'qnr-kpi-drafts',
                onClick: () => showList('draft') },
            ];
            return boxes.map((b, i) => (
              <Card key={b.key} onClick={b.onClick} className={`p-4 shadow-card ${b.onClick ? 'cursor-pointer transition-smooth hover:shadow-md' : ''}`} data-testid={b.testid}>
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wider font-bold text-muted-foreground">{i + 1}</span>
                  <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{b.label}</span>
                </div>
                <p className={`font-display text-3xl font-bold tabular-nums mt-1 ${b.tone}`}>{b.value}</p>
              </Card>
            ));
          })()}
        </div>

        <LibraryTypeBar items={items} />

        <div className="flex items-center justify-between gap-2 flex-wrap">
          <Tabs value={viewMode} onValueChange={setViewMode}>
            <TabsList data-testid="library-view-toggle">
              <TabsTrigger value="cards" data-testid="library-view-cards">{t('library.list.viewCards', { defaultValue: 'Cards' })}</TabsTrigger>
              <TabsTrigger value="list" data-testid="library-view-list">{t('library.list.viewList', { defaultValue: 'List' })}</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/admin/questionnaires/import')}
              className="h-10 gap-1.5"
              data-testid="qnr-import-btn"
            >
              <Upload className="h-4 w-4" /> {t('library.import.title', { defaultValue: 'Import' })}
            </Button>
            <Button data-testid="qnr-new-btn" onClick={() => navigate('/admin/studio')} className="bg-gradient-primary h-10 gap-1.5"><Plus className="h-4 w-4" /> {t('library.createInStudio', { defaultValue: 'Crear en el Studio' })}</Button>
          </div>
        </div>

        {viewMode === 'cards' ? (
          <LibrarySearchExplorer isAdmin={isAdmin} />
        ) : (
          <LibraryListView
            items={items}
            isAdmin={isAdmin}
            statusFilter={listStatus}
            onStatusFilter={setListStatus}
            reload={reload}
          />
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('pages.questionnaires.newQuestionnaireTitle')}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3">
              <div>
                <Label>{t('pages.questionnaires.name')}</Label>
                <Input data-testid="qnr-form-name" className="mt-1.5" placeholder={t('pages.questionnaires.namePh')} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Label>{t('pages.questionnaires.code')}</Label>
                <Input className="mt-1.5" placeholder={slug(form.name) || t('pages.questionnaires.codeHint')} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
              </div>
              <div>
                <Label>{t('pages.questionnaires.description')}</Label>
                <Textarea className="mt-1.5" placeholder={t('pages.questionnaires.descriptionHint')} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div>
                <Label>{t('pages.questionnaires.suite')}</Label>
                <Select value={form.suite_codes[0]} onValueChange={(v) => setForm({ ...form, suite_codes: [v] })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{suiteOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <p className="text-xs text-muted-foreground">{t('pages.questionnaires.afterCreateHint')}</p>
            </div>
          )}
          <DialogFooter><Button variant="ghost" onClick={() => setOpen(false)}>{t('pages.questionnaires.cancel')}</Button><Button data-testid="qnr-form-save" onClick={create} className="bg-gradient-primary">{t('pages.questionnaires.create')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Editor dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editor?.name} — {t('pages.questionnaires.editorTitle')}</DialogTitle></DialogHeader>
          {editor && (
            <Tabs defaultValue="sections">
              <TabsList>
                <TabsTrigger value="sections">{t('pages.questionnaires.sections')} ({editor.sections.length})</TabsTrigger>
                <TabsTrigger value="questions">{t('pages.questionnaires.questions')} ({editor.questions.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="sections" className="space-y-2 mt-4">
                <div className="grid grid-cols-12 gap-2 px-2 text-xs uppercase tracking-wider font-semibold text-muted-foreground" data-testid="qnr-sections-headers">
                  <div className="col-span-7">{t('pages.questionnaires.thSection.name')}</div>
                  <div className="col-span-2">{t('pages.questionnaires.thSection.weight')}</div>
                  <div className="col-span-2">{t('pages.questionnaires.thSection.order')}</div>
                  <div className="col-span-1 text-right">{t('pages.questionnaires.thSection.remove')}</div>
                </div>
                {editor.sections.map((s, i) => (
                  <div key={s.id} className="grid grid-cols-12 gap-2 items-start p-2 border rounded-lg">
                    <div className="col-span-7 space-y-2">
                      <div>
                        <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                          {t('pages.questionnaires.thSection.name')}
                        </Label>
                        <Input className="h-9 mt-1" value={s.name} onChange={(e) => updateSection(i, { name: e.target.value })} />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                          {t('library.editor.sectionDescription', { defaultValue: 'Description (shown to candidates before this section starts)' })}
                        </Label>
                        <Textarea
                          className="mt-1 text-sm min-h-[60px]"
                          data-testid={`qnr-section-${s.id}-description`}
                          value={s.description || ''}
                          onChange={(e) => updateSection(i, { description: e.target.value })}
                          placeholder={t('library.editor.sectionDescriptionPh', { defaultValue: 'Briefly explain what this section measures…' })}
                        />
                      </div>
                    </div>
                    <TooltipProvider delayDuration={150}>
                      <div className="col-span-2">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground cursor-help underline decoration-dotted decoration-from-font underline-offset-2">
                              {t('pages.questionnaires.thSection.weight')}
                            </Label>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs text-xs leading-relaxed">
                            {t('library.editor.sectionWeightHint', { defaultValue: 'How strongly this section contributes to the overall evaluation score relative to other sections.' })}
                          </TooltipContent>
                        </Tooltip>
                        <Input className="h-9 mt-1" type="number" step={0.1} value={s.weight} onChange={(e) => updateSection(i, { weight: parseFloat(e.target.value) || 0 })} />
                      </div>
                      <div className="col-span-2">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground cursor-help underline decoration-dotted decoration-from-font underline-offset-2">
                              {t('pages.questionnaires.thSection.order')}
                            </Label>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs text-xs leading-relaxed">
                            {t('library.editor.sectionOrderHint', { defaultValue: 'Display order — lower numbers appear first.' })}
                          </TooltipContent>
                        </Tooltip>
                        <Input className="h-9 mt-1" type="number" value={s.order} onChange={(e) => updateSection(i, { order: parseInt(e.target.value || '0', 10) })} />
                      </div>
                    </TooltipProvider>
                    <Button size="sm" variant="ghost" className="col-span-1 h-9 w-9 p-0 text-muted-foreground hover:text-destructive mt-5" onClick={() => removeSection(i)}><Minus className="h-3.5 w-3.5" /></Button>
                  </div>
                ))}
                <Button size="sm" onClick={addSection} variant="outline" className="gap-1"><Plus className="h-3.5 w-3.5" /> {t('pages.questionnaires.addSection')}</Button>
              </TabsContent>

              <TabsContent value="questions" className="space-y-3 mt-4">
                {editor.sections.map((s) => {
                  const sectionQs = editor.questions.filter((q) => q.section_id === s.id);
                  const isOpen = expanded[s.id] !== false; // default open
                  return (
                    <Card key={s.id} className="p-4">
                      <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpanded({ ...expanded, [s.id]: !isOpen })}>
                        <div className="flex items-center gap-2">
                          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          <h4 className="font-display font-bold">{s.name}</h4>
                          <Badge variant="outline" className="text-xs">{sectionQs.length} q</Badge>
                        </div>
                        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); addQuestion(s.id); }} className="gap-1"><Plus className="h-3.5 w-3.5" /> {t('pages.questionnaires.addQuestion')}</Button>
                      </div>
                      {isOpen && sectionQs.map((q) => (
                        <div key={q.id} className="mt-3 border rounded-lg p-3 space-y-2">
                          <div className="flex gap-2">
                            <Textarea className="flex-1 min-h-[44px] text-sm" value={q.prompt} onChange={(e) => updateQuestion(q.id, { prompt: e.target.value })} />
                            <div className="flex flex-col gap-1">
                              <TooltipProvider delayDuration={150}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground cursor-help underline decoration-dotted decoration-from-font underline-offset-2">
                                      {t('pages.questionnaires.weight')}
                                    </Label>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs text-xs leading-relaxed">
                                    {t('library.editor.questionWeightHint', { defaultValue: 'This value represents the relative importance of the question when calculating the final score.' })}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              <Input className="h-8 w-20 text-xs" type="number" step={0.1} value={q.weight} onChange={(e) => updateQuestion(q.id, { weight: parseFloat(e.target.value) || 0 })} />
                              <Button size="sm" variant="ghost" className="h-8 w-20 text-xs text-muted-foreground hover:text-destructive" onClick={() => removeQuestion(q.id)}><Minus className="h-3.5 w-3.5 mr-1" /> {t('pages.questionnaires.remove')}</Button>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                              {t('library.editor.questionType', { defaultValue: 'Question type' })}
                            </Label>
                            <Select value={q.type || 'single_choice'} onValueChange={(v) => updateQuestion(q.id, { type: v })}>
                              <SelectTrigger className="h-8 w-[200px] text-xs" data-testid={`qnr-question-type-${q.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="single_choice">{t('library.editor.types.single_choice', { defaultValue: 'Single choice' })}</SelectItem>
                                <SelectItem value="multi_choice">{t('library.editor.types.multi_choice', { defaultValue: 'Multiple choice' })}</SelectItem>
                                <SelectItem value="likert_5">{t('library.editor.types.likert_5', { defaultValue: 'Likert (1-5)' })}</SelectItem>
                                <SelectItem value="scale_1_10">{t('library.editor.types.scale_1_10', { defaultValue: 'Scale (1-10)' })}</SelectItem>
                                <SelectItem value="text">{t('library.editor.types.text', { defaultValue: 'Short text' })}</SelectItem>
                                <SelectItem value="long_text">{t('library.editor.types.long_text', { defaultValue: 'Long text' })}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('pages.questionnaires.options')}</Label>
                            {(q.type === 'text' || q.type === 'long_text') ? (
                              <p className="text-xs text-muted-foreground italic" data-testid={`qnr-question-${q.id}-text-hint`}>
                                {t('library.editor.textTypeHint', { defaultValue: 'Open-ended question — no answer options or correct-answer selector.' })}
                              </p>
                            ) : (
                              <>
                                {q.options.map((o, oi) => (
                                  <div key={o.id} className="grid grid-cols-12 gap-2">
                                    <Input className="col-span-11 h-8 text-xs" value={o.label} onChange={(e) => updateOpt(q.id, oi, { label: e.target.value })} data-testid={`opt-label-${o.id}`} />
                                    <Button size="sm" variant="ghost" className="col-span-1 h-8 w-8 p-0 text-muted-foreground hover:text-destructive" onClick={() => removeOpt(q.id, oi)}><Minus className="h-3.5 w-3.5" /></Button>
                                  </div>
                                ))}
                                <Button size="sm" variant="ghost" onClick={() => addOpt(q.id)} className="gap-1 text-xs"><Plus className="h-3 w-3" /> {t('pages.questionnaires.addOption')}</Button>
                                {(q.type === 'single_choice' || q.type === 'multi_choice') && q.options.length > 0 && (
                                  <div className="pt-3 mt-2 border-t">
                                    <CorrectAnswerRadio
                                      variant={q.type === 'single_choice' ? 'single' : 'multi'}
                                      options={q.options}
                                      onChange={(newOptions) => updateQuestion(q.id, { options: newOptions })}
                                      testIdPrefix={`correct-${q.id}`}
                                    />
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                          <div>
                            <TooltipProvider delayDuration={150}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground cursor-help underline decoration-dotted decoration-from-font underline-offset-2">
                                    {t('pages.questionnaires.linkedComps')}
                                  </Label>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs text-xs leading-relaxed">
                                  {t('library.editor.competencyWeightHint', { defaultValue: 'Each numeric value (0–1) represents how strongly this question measures the selected competency.' })}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <div className="mt-1.5 flex flex-wrap gap-1.5">
                              {comps.filter((c) => !c.archived).map((c) => {
                                const w = (q.competency_map || {})[c.code];
                                const active = w !== undefined;
                                return (
                                  <button
                                    key={c.code}
                                    onClick={() => setCompMap(q.id, c.code, active ? null : 1.0)}
                                    className={`text-xs px-2 py-0.5 rounded-full border transition ${active ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:bg-muted/40'}`}
                                  >
                                    {c.name}{active ? ` × ${w}` : ''}
                                  </button>
                                );
                              })}
                            </div>
                            {Object.keys(q.competency_map || {}).length > 0 && (
                              <div className="mt-2 grid grid-cols-2 gap-1">
                                {Object.entries(q.competency_map || {}).map(([code, w]) => (
                                  <div key={code} className="flex items-center gap-2 text-xs">
                                    <span className="font-mono">{code}</span>
                                    <Input type="number" step={0.1} min={0} max={1} value={w} onChange={(e) => setCompMap(q.id, code, parseFloat(e.target.value) || 0)} className="h-7 w-20 text-xs" />
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </Card>
                  );
                })}
              </TabsContent>
            </Tabs>
          )}
          <DialogFooter><Button variant="ghost" onClick={() => setEditorOpen(false)}>{t('pages.questionnaires.cancelDraft')}</Button><Button data-testid="qnr-editor-save" onClick={saveDraft} className="bg-gradient-primary">{t('pages.questionnaires.saveDraft')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* UAT 07/14 P0-2 — Request revision reason dialog */}
      <Dialog open={!!revisionDialog.q} onOpenChange={(o) => !o && setRevisionDialog({ q: null, reason: '' })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('pages.questionnaires.requestRevisionTitle', { defaultValue: 'Request revision' })}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t('pages.questionnaires.requestRevisionDesc', { defaultValue: 'Explain what the author must change. The evaluation returns to "Needs revision" and becomes editable again.' })}
          </p>
          <Textarea
            value={revisionDialog.reason}
            onChange={(e) => setRevisionDialog((d) => ({ ...d, reason: e.target.value }))}
            rows={4}
            placeholder={t('pages.questionnaires.requestRevisionPh', { defaultValue: 'Reason (minimum 10 characters)…' })}
            data-testid="qnr-revision-reason"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRevisionDialog({ q: null, reason: '' })}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
            <Button onClick={submitRevision} className="bg-gradient-primary" data-testid="qnr-revision-submit">
              {t('pages.questionnaires.requestRevision', { defaultValue: 'Request revision' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function FilterSelect({ value, onChange, options, label, testId }) {
  return (
    <div className="space-y-1">
      <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-9 w-[160px]" data-testid={testId} aria-label={label}>
          <SelectValue placeholder={label} />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function VocabFilterSelect({ value, onChange, vocab, allLabel, label, testId }) {
  const { i18n } = useTranslation();
  const isEN = (i18n.language || '').toLowerCase().startsWith('en');
  // Prepend the "all" sentinel, but skip any vocab row whose code itself
  // is `all` (the `level` vocab contains code='all' = "All levels") so
  // we don't collide on React keys / Select values.
  const opts = [
    { value: 'all', label: allLabel },
    ...((vocab || []).filter((v) => v.code !== 'all').map((v) => ({
      value: v.code,
      label: isEN ? (v.label_en || v.code) : (v.label_es || v.label_en || v.code),
    }))),
  ];
  return <FilterSelect value={value} onChange={onChange} options={opts} label={label} testId={testId} />;
}
